import * as React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  COLLABORATOR_INDICATOR_SIZE_PX,
  COLLABORATOR_INDICATOR_STEP_PX,
  MAX_VISIBLE_COLLABORATOR_INDICATORS,
} from '@/drafting/constants';
import {
  draftCollaboratorIdentityKey,
  draftCollaboratorInitials,
  draftCollaboratorTitle,
} from '@/drafting/utils/collaborators';
import type { DraftCollaborator } from '@/drafting/hooks/useDraftCollaboration';
import { cn } from '@/lib/utils';

export function LiveCollaboratorIndicators({
  collaborators,
}: {
  collaborators: DraftCollaborator[];
}) {
  const ariaLabel = `Live editors: ${collaborators.map(draftCollaboratorTitle).join(', ')}`;
  const indicatorItems = React.useMemo(() => {
    const visibleCollaborators = collaborators.slice(0, MAX_VISIBLE_COLLABORATOR_INDICATORS);
    const hiddenCount = collaborators.length - visibleCollaborators.length;
    return [
      ...visibleCollaborators.map((collaborator) => ({
        color: collaborator.color,
        initials: draftCollaboratorInitials(collaborator),
        key: draftCollaboratorIdentityKey(collaborator),
        label: draftCollaboratorTitle(collaborator),
        overflow: false,
      })),
      ...(hiddenCount > 0
        ? [{
          color: null,
          initials: `+${hiddenCount}`,
          key: 'hidden-collaborators',
          label: ariaLabel,
          overflow: true,
        }]
        : []),
    ];
  }, [ariaLabel, collaborators]);
  const [hoveredLabel, setHoveredLabel] = React.useState(indicatorItems[0]?.label ?? ariaLabel);

  const updateHoveredLabel = React.useCallback((event: React.PointerEvent<HTMLSpanElement>) => {
    if (indicatorItems.length === 0) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const nearestIndex = Math.round((x - COLLABORATOR_INDICATOR_SIZE_PX / 2) / COLLABORATOR_INDICATOR_STEP_PX);
    const clampedIndex = Math.max(0, Math.min(indicatorItems.length - 1, nearestIndex));
    setHoveredLabel(indicatorItems[clampedIndex]?.label ?? ariaLabel);
  }, [ariaLabel, indicatorItems]);

  if (collaborators.length < 2) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex shrink-0 items-center -space-x-1"
            role="img"
            aria-label={ariaLabel}
            onPointerEnter={updateHoveredLabel}
            onPointerMove={updateHoveredLabel}
            onPointerLeave={() => setHoveredLabel(indicatorItems[0]?.label ?? ariaLabel)}
          >
            {indicatorItems.map((item) => (
              <span
                key={item.key}
                className={cn(
                  'inline-flex h-5 w-5 items-center justify-center rounded-full border border-white text-[9px] leading-none shadow-[0_0_0_1px_rgba(15,23,42,0.10)]',
                  item.overflow
                    ? 'bg-slate-100 font-semibold text-slate-500'
                    : 'font-bold text-white',
                )}
                style={item.color ? { backgroundColor: item.color } : undefined}
              >
                {item.initials}
              </span>
            ))}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs font-medium">
          {hoveredLabel}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
